import { ScrollToApplyButton } from '#/components/ApplyButton/ScrollToApplyButton';
import { Divider } from '#/components/Divider/Divider';
import { Hide } from '#/components/Hide/Hide';
import { Text } from '#/components/Text/Text';
import { Tooltip } from '#/components/Tooltip/Tooltip';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { ListingTiming } from '#/modules/listing/helpers/listingTiming';
import { ListingViewStatus } from '#/modules/listing/types/ListingViewStatus';
import { timeago } from '#/utils/string/datetime/timeago';

const APPLY_EVENT_NAME = {
  JOB: 'Apply Button on Job Listing (Sidebar)',
  INTERNSHIP: 'Apply Button on Internship Listing (Sidebar)',
  VOLOP: 'I Want to Help Button on Volop Listing (Sidebar)',
  EVENT: 'Join Button on Event Listing (Sidebar)',
};

type ListingSidebarContentProps = {
  timing: ListingTiming;
  listingViewStatus: ListingViewStatus;
  showScrollToApplyButton: boolean;
  listingType: 'JOB' | 'INTERNSHIP' | 'VOLOP' | 'EVENT';
  applied: boolean;
};

export function ListingViewSidebarContent({
  timing,
  listingViewStatus,
  showScrollToApplyButton,
  listingType,
  applied,
}: ListingSidebarContentProps) {
  const { i18n, locale } = useI18n();

  const applyEventName = APPLY_EVENT_NAME[listingType];

  return (
    <>
      {timing.publishedDate && (
        <div>
          {timing.isNew && (
            <>
              <Text
                fontWeight="semiBold"
                color="mediumContentGrey"
                borderBottom="4px solid"
                borderColor="brandYellow"
                display="inline-block"
                lineHeight="6px"
                ml="0px"
                mr="12px"
                p="0px"
              >
                {i18n('New')}
              </Text>
              <Text
                display={[
                  'inline-block',
                  'inline-block',
                  'inline-block',
                  'none',
                ]}
                color="lightContentGrey"
                pl={2}
                pr={2}
              >
                |
              </Text>
            </>
          )}
          <Text display="inline-block" color="lightContentGrey">
            {
              /* TRANSLATORS: Listing page - time published */
              i18n('Published %(timeAgo)s', {
                timeAgo: timeago(timing.publishedDate, {
                  now: timing.now,
                  locale,
                }),
              })
            }
          </Text>
        </div>
      )}

      {listingType !== 'EVENT' &&
        listingViewStatus !== ListingViewStatus.EXPIRED &&
        showScrollToApplyButton && (
          <Hide xs sm md>
            <Tooltip
              content={i18n("You can't take this action in Preview Mode")}
              triggerCondition={listingViewStatus === ListingViewStatus.PREVIEW}
              variant="filled"
              placement="bottom-center"
              size="large"
            >
              <div
                style={{
                  marginTop: 24,
                  cursor:
                    listingViewStatus === ListingViewStatus.PREVIEW
                      ? 'not-allowed'
                      : undefined,
                  pointerEvents:
                    listingViewStatus === ListingViewStatus.PREVIEW
                      ? 'none'
                      : undefined,
                }}
              >
                <ScrollToApplyButton
                  tabIndex={
                    listingViewStatus === ListingViewStatus.PREVIEW
                      ? -1
                      : undefined
                  }
                  applied={applied}
                  eventName={applyEventName}
                  qaId="easy-apply-button-sidebar"
                >
                  {listingType === 'VOLOP' ? i18n('I Want to Help') : undefined}
                </ScrollToApplyButton>
              </div>
            </Tooltip>
          </Hide>
        )}

      <Divider marginTop={24} marginBottom={24} />
    </>
  );
}
